import { useContext } from "react";
import EmailStep from "./EmailStep";
import NameStep from "./NameStep";
import GenderStep from "./GenderStep";
import HeightStep from "./HeightStep";
import { OnboardingStep } from "../../utils/enums";
import { Step } from "../../types/interfaces";
import { LanguageContext } from "../../utils/context/languageContext";
import { LanguageKeys } from "../../utils/languageKeys";
import ProgressDots from "../../atoms/progressDots/ProgressDots";
import { useConfig } from "../../utils/context/configContext";

const StepsWrapper = ({ currentStep, onStepDone, stepOrder, visibleSteps = [] }: { currentStep: Step; onStepDone: (val: any) => void; stepOrder: any; visibleSteps?: Step[] }) => {
	const { translate } = useContext(LanguageContext) || {};
	const resolvedConfig = useConfig();

	const renderStep = () => {
		if (!currentStep) return null;

		const commonProps = {
			resolvedConfig,
			initialValue: currentStep.value,
			onComplete: onStepDone,
			onStepComplete: currentStep?.onStepComplete,
		};

		switch (currentStep.type) {
			case OnboardingStep.Email:
				return <EmailStep {...commonProps} />;
			case OnboardingStep.Name:
				return <NameStep {...commonProps} />;
			case OnboardingStep.Gender:
				return <GenderStep {...commonProps} />;
			case OnboardingStep.Height:
				return <HeightStep {...commonProps} />;
			default:
				return (
					<div>
						{translate?.(LanguageKeys.noValidSteps)} {currentStep.type}
					</div>
				);
		}
	};

	if (!visibleSteps.length) return <div>{translate?.(LanguageKeys.unsupportedStep)}</div>;

	// stepOrder is the resolved visible-only list (from Onboarding.tsx),
	// so indexOf gives the correct 0-based position in the actual navigation flow.
	const absoluteIndex = currentStep ? stepOrder.indexOf(currentStep.type) : 0;

	return (
		<div data-testid="onboarding-root" data-onboarding-step={absoluteIndex}>
			<div id="swan-onboarding-bg" style={{ background: resolvedConfig?.style?.base?.backgroundColor }} className="fixed common-ui-main w-full h-full z-[0] pointer-events-none top-0 left-0"></div>

			<div className="px-[15px] common-ui-main relative pt-[15px] w-full flex items-center flex-col">
				<div className="max-w-[28rem] mx-auto w-full">
					{/* Logo */}
					{resolvedConfig?.logo && (
						<div className="text-center mb-[1rem] flex justify-center">
							<img
								src={resolvedConfig?.logo}
								alt="logo"
								style={{
									height: resolvedConfig?.style?.logo?.logoHeight,
									width: resolvedConfig?.style?.logo?.logoWidth,
								}}
								className="h-10 mx-auto"
							/>
						</div>
					)}

					{/* Progress Dots */}
					<ProgressDots totalSteps={stepOrder.length} currentStepIndex={absoluteIndex} />

					{/* Heading */}
					<h1
						style={{
							fontFamily: resolvedConfig?.style?.heading?.headingFontFamily || "SeriouslyNostalgic Fn",
							fontSize: resolvedConfig?.style?.heading?.headingFontSize || "32px",
							color: resolvedConfig?.style?.heading?.headingColor || "#000",
							fontWeight: resolvedConfig?.style?.heading?.headingFontWeight || "normal",
						}}
						className="text-center pt-[1.5rem]"
					>
						{translate?.(LanguageKeys.onboarding[currentStep?.type || "heading"])}
					</h1>

					{/* Step */}
					{renderStep()}
				</div>
			</div>
		</div>
	);
};

export default StepsWrapper;
